import React, { useState } from 'react';
import { getSourceDisplayName, getSourceUrl } from '../utils/sourceRegistry';
import DocViewerModal from './DocViewerModal';

function DecisionLogViewer({ logs }) {
  const [filter, setFilter] = useState('all');
  const [expandedLog, setExpandedLog] = useState(null);
  const [docViewer, setDocViewer] = useState({ open: false, url: '', title: '' });

  const filteredLogs = filter === 'all'
    ? logs
    : logs.filter((log) => log.agent_id === filter);

  const agentIds = [...new Set(logs.map((l) => l.agent_id))];

  const renderMarkdown = (text) => {
    const rawText = text || "";
    try {
      if (window.marked) {
        return { __html: window.marked.parse(rawText) };
      }
    } catch (e) {
      console.error("Markdown Error", e);
    }
    return { __html: rawText };
  };

  if (logs.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
        No decision logs yet.
      </div>
    );
  }

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>
        <button
          className={`btn btn--sm ${filter === 'all' ? 'btn--primary' : 'btn--outline'}`}
          onClick={() => setFilter('all')}
        >
          All ({logs.length})
        </button>
        {agentIds.map((agentId) => (
          <button
            key={agentId}
            className={`btn btn--sm ${filter === agentId ? 'btn--primary' : 'btn--outline'}`}
            onClick={() => setFilter(agentId)}
          >
            {agentId}
          </button>
        ))}
      </div>

      {/* Log table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{
              borderBottom: '1px solid var(--border-color)',
              textAlign: 'left',
            }}>
              <th style={{ padding: '8px 12px', color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>
                Time
              </th>
              <th style={{ padding: '8px 12px', color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>
                Agent
              </th>
              <th style={{ padding: '8px 12px', color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>
                Reasoning
              </th>
              <th style={{ padding: '8px 12px', color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', textAlign: 'right' }}>
                Conf.
              </th>
              <th style={{ padding: '8px 12px', color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', textAlign: 'center' }}>
                Details
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.map((log) => (
              <React.Fragment key={log.log_id}>
                <tr
                  onClick={() => setExpandedLog(expandedLog === log.log_id ? null : log.log_id)}
                  style={{
                    borderBottom: '1px solid var(--border-color)',
                    transition: 'background 150ms ease',
                    cursor: 'pointer',
                    background: expandedLog === log.log_id ? 'var(--bg-card-hover)' : 'transparent'
                  }}
                  onMouseEnter={(e) => { if(expandedLog !== log.log_id) e.currentTarget.style.background = 'var(--bg-card-hover)' }}
                  onMouseLeave={(e) => { if(expandedLog !== log.log_id) e.currentTarget.style.background = 'transparent' }}
                >
                  <td style={{ padding: '10px 12px', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {new Date(log.timestamp).toLocaleTimeString()}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{
                      fontSize: 11,
                      padding: '2px 8px',
                      borderRadius: 'var(--radius-full)',
                      background: log.agent_id === 'supervisor' ? 'var(--color-safe-bg)' :
                                  log.agent_id === 'rules-fallback' ? 'var(--color-warning-bg)' :
                                  'var(--color-info-bg)',
                      color: log.agent_id === 'supervisor' ? 'var(--color-safe)' :
                             log.agent_id === 'rules-fallback' ? 'var(--color-warning)' :
                             'var(--color-info)',
                    }}>
                      {log.agent_id}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 12, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>
                    {log.reasoning || log.output_summary}
                  </td>
                  <td style={{
                    padding: '10px 12px',
                    textAlign: 'right',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 12,
                    color: log.confidence > 0.7 ? 'var(--color-safe)' :
                           log.confidence > 0.5 ? 'var(--color-warning)' : 'var(--color-danger)',
                  }}>
                    {(log.confidence * 100).toFixed(0)}%
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                    <button className="btn btn--sm btn--outline" style={{ padding: '2px 8px', fontSize: 10 }}>
                      {expandedLog === log.log_id ? 'Hide' : 'View'}
                    </button>
                  </td>
                </tr>
                {expandedLog === log.log_id && (
                  <tr>
                    <td colSpan="5" style={{ padding: '16px 24px', background: 'var(--bg-card-hover)', borderBottom: '1px solid var(--border-color)' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <div>
                          <h4 style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>Internal Reasoning</h4>
                          <div 
                            className="reasoning-rich-text"
                            style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-primary)' }}
                            dangerouslySetInnerHTML={renderMarkdown(log.reasoning)} 
                          />
                        </div>
                        
                        <div>
                          <h4 style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>Output Summary</h4>
                          <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>{log.output_summary}</div>
                        </div>

                        {log.sources && log.sources.length > 0 && (
                          <div>
                            <h4 style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 4 }}>Knowledge base used (RAG)</h4>
                            <p style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 8 }}>Specific documents used for this decision. Click to open:</p>
                            <ul className="rag-sources-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                              {log.sources.map((src, idx) => {
                                const filename = typeof src === 'string' ? src.trim() : String(src);
                                const displayName = getSourceDisplayName(filename);
                                const href = getSourceUrl(filename);
                                return (
                                  <li key={idx} className="rag-source-item" style={{
                                    fontSize: 12,
                                    fontFamily: 'var(--font-mono)',
                                    padding: '4px 0',
                                    borderBottom: '1px solid var(--border-color)',
                                    color: 'var(--text-primary)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 8,
                                    flexWrap: 'wrap',
                                  }}>
                                    <span className="rag-source-item__name">{displayName}</span>
                                    <button type="button" className="rag-source-item__link" style={{ color: 'var(--color-accent)', textDecoration: 'none', fontSize: 11 }} onClick={() => setDocViewer({ open: true, url: href, title: displayName })}>View document →</button>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', paddingTop: 8, borderTop: '1px solid var(--border-color)' }}>
                          <strong>Inputs:</strong> {log.inputs_summary}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <DocViewerModal isOpen={docViewer.open} url={docViewer.url} title={docViewer.title} onClose={() => setDocViewer({ open: false, url: '', title: '' })} />
    </div>
  );
}

export default DecisionLogViewer;
